// ── ASK THE PORTAL ──────────────────────────────────────────────────
// The floating assistant: a launcher in the bottom-right corner and a
// right-hand drawer that answers questions from the staff guides.
//
// This started life inside the Help page, which was the wrong home for it --
// the questions arrive while you're mid-task on some other screen, and going
// to Help to ask about the screen you just left is exactly the interruption
// the feature is supposed to remove.
//
// Three consequences of living in the shell rather than in a view:
//
// * It mounts ONCE, outside #main. route() replaces #main.innerHTML on every
//   navigation, so a panel rendered inside a view is destroyed by the next
//   click -- which is what happened to the old one. #modal and #toast already
//   sit outside the shell for the same reason; this joins them.
// * Only the thread re-renders when an answer lands, never the whole panel.
//   The old version re-rendered everything and then had to put the caret back
//   by hand; here the input is simply never replaced.
// * It knows what screen you're on, and sends that along, so "how do I do
//   this?" resolves against Coaching → Assignment board without you saying so.
//   That context goes in the user turn, never the system prompt -- see the
//   cache-prefix warning in src/HelpAssistant.php.
//
// Deliberately hidden for view-only admins and in parent mode: every guide it
// answers from is already `editOnly`, and portal.html is shared with parents.
import { apiFetch } from './api.js';
import { session, sessionMode, currentView } from './state.js';
import { canEdit } from './domain-utils.js';
import { NAV } from './routing.js';
import { openHelpDoc } from '../views/admin-help.js';

const ASK = { open: false, busy: false, thread: [] };

// ── Visibility & screen context ─────────────────────────────────────

function isAvailable() {
  return !!session && sessionMode === 'admin' && canEdit(session) && ASK.enabled;
}

// Called by route() on every navigation (via window, to avoid a cycle with
// routing.js). Cheap enough to run on every route change.
export function syncAssistant() {
  const launcher = document.getElementById('assistantLauncher');
  const panel = document.getElementById('assistant');
  if (!launcher || !panel) return;

  const show = isAvailable();
  launcher.hidden = !show;
  if (!show) closeAssistant();
}

// "Coaching → Assignment board" for the view we're on, derived from NAV so
// there's one list of screen names rather than a second copy here that drifts.
//
// Not displayed anywhere — it rides along with the question so "how do I do
// this?" resolves against the screen she's on. It looked like chrome in the
// header and read as clutter; it does its work in the request.
function currentScreenLabel() {
  const view = currentView || '';
  for (const item of NAV.admin) {
    if (item.children) {
      const child = item.children.find(c => c.id === view);
      if (child) return `${item.label} → ${child.label}`;
    }
    if (item.id === view) return item.label;
  }
  return '';
}

// ── Open / close ────────────────────────────────────────────────────

export function openAssistant() {
  if (!isAvailable()) return;
  ASK.open = true;
  document.getElementById('assistant').classList.add('open');
  document.getElementById('assistantLauncher').setAttribute('aria-expanded', 'true');
  document.getElementById('assistantInput')?.focus();
}

export function closeAssistant() {
  ASK.open = false;
  document.getElementById('assistant')?.classList.remove('open');
  document.getElementById('assistantLauncher')?.setAttribute('aria-expanded', 'false');
}

export function toggleAssistant() {
  ASK.open ? closeAssistant() : openAssistant();
}

// ── Rendering ───────────────────────────────────────────────────────

function renderThread() {
  const el = document.getElementById('assistantThread');
  if (!el) return;

  if (!ASK.thread.length && !ASK.busy) {
    el.innerHTML = `
      <div class="assistant-empty">
        <p>Ask anything about running the portal.</p>
      </div>`;
    return;
  }

  el.innerHTML = ASK.thread.map(renderTurn).join('')
    + (ASK.busy ? `
      <div class="assistant-thinking" role="status" aria-live="polite">
        <span>Thinking</span><i></i><i></i><i></i>
      </div>` : '');

  wireSources();
  el.scrollTop = el.scrollHeight;
}

function renderTurn(turn) {
  if (turn.role === 'user') {
    return `<div class="assistant-q">${esc(turn.text)}</div>`;
  }
  if (turn.role === 'error') {
    return `<div class="assistant-a assistant-error">${esc(turn.text)}</div>`;
  }
  return `
    <div class="assistant-a">
      ${renderBody(turn.text)}
      ${renderSources(turn.sources)}
    </div>`;
}

// Answers come back as plain text carrying a little light markdown: the model
// reaches for "- " bullets and **bold** labels when one answer has to cover
// several cases, which is behaviour the prompt asks for.
//
// Deliberately not a markdown library. esc() runs on every fragment BEFORE any
// marker is promoted to a tag, so the only HTML that can reach the page is the
// <strong>/<li> written here.
function renderBody(text) {
  const inline = s => esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  const out = [];
  let para = [];
  let list = [];
  const flushPara = () => {
    if (para.length) out.push(`<p>${para.map(inline).join('<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list.length) out.push(`<ul>${list.map(l => `<li>${inline(l)}</li>`).join('')}</ul>`);
    list = [];
  };

  text.split('\n').forEach(line => {
    // A bullet can follow a lead-in on the previous line ("Where rates live:"),
    // so paragraphs and lists flush independently rather than only at a blank
    // line.
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) { flushPara(); list.push(bullet[1]); return; }
    if (line.trim() === '') { flushPara(); flushList(); return; }
    flushList();
    para.push(line);
  });
  flushPara();
  flushList();

  return out.join('');
}

// Just the guide, linked. This used to quote the passages the answer leaned
// on, which was useful while we were still checking whether it was grounded at
// all -- but it restated the answer underneath the answer, and a drawer full of
// blockquotes buries the thing she actually asked for. The answer stands on its
// own; the link is there to back it up when she wants the whole procedure.
function renderSources(sources) {
  if (!sources || !sources.length) return '';

  return `
    <div class="assistant-sources">
      <div class="assistant-sources-label">${sources.length === 1 ? 'Source' : 'Sources'}</div>
      <div class="assistant-source-links">
        ${sources.map(s => `<button data-assistant-slug="${esc(s.slug)}">${esc(s.title)}</button>`).join('')}
      </div>
    </div>`;
}

// Opening a guide closes the drawer -- the guide renders in #main underneath
// it, and leaving the panel over the thing you just asked to read is worse
// than losing the (still intact) thread behind the launcher.
function wireSources() {
  document.querySelectorAll('[data-assistant-slug]').forEach(btn => {
    btn.addEventListener('click', () => {
      closeAssistant();
      openHelpDoc(btn.dataset.assistantSlug);
    });
  });
}

// ── The input ───────────────────────────────────────────────────────

// Grow the box to fit what's been typed, up to the CSS max-height, after which
// it scrolls.
//
// Two things this has to get right, and both are silent when wrong:
//
// * Height is reset to 'auto' before measuring. scrollHeight otherwise only
//   ever reports the height the box already has, so it grows and never shrinks.
// * The border is added back on. Everything here is border-box, so `height`
//   includes the border while scrollHeight does not — setting one to the other
//   leaves the content box short by exactly the border, which overflows by 2px
//   and shows a scrollbar on a two-line question that plainly fits.
function autosize() {
  const el = document.getElementById('assistantInput');
  if (!el) return;
  el.style.height = 'auto';
  const border = el.offsetHeight - el.clientHeight;
  el.style.height = (el.scrollHeight + border) + 'px';
}

// A textarea takes Enter as a newline, so submitting needs saying explicitly.
// Enter sends and Shift+Enter breaks the line, which is the convention every
// chat box has trained people on.
function onKeydown(e) {
  if (e.key !== 'Enter' || e.shiftKey) return;
  e.preventDefault();
  submit();
}

// ── Asking ──────────────────────────────────────────────────────────

function submit() {
  const input = document.getElementById('assistantInput');
  const question = (input?.value || '').trim();
  if (!question || ASK.busy) return;

  const history = ASK.thread
    .filter(t => t.role === 'user' || t.role === 'assistant')
    .map(t => ({ role: t.role, text: t.text }));

  ASK.thread.push({ role: 'user', text: question });
  ASK.busy = true;
  input.value = '';
  autosize();
  input.disabled = true;
  renderThread();

  apiFetch('/help/ask', {
    method: 'POST',
    body: JSON.stringify({ question, history, screen: currentScreenLabel() }),
  })
    .then(res => ASK.thread.push({ role: 'assistant', text: res.answer, sources: res.sources }))
    // Kept in the thread rather than fired as a toast: the question is sitting
    // right above it, and a toast would vanish before she looked up.
    .catch(e => ASK.thread.push({ role: 'error', text: e.message }))
    .finally(() => {
      ASK.busy = false;
      input.disabled = false;
      renderThread();
      input.focus();
    });
}

// ── Mount ───────────────────────────────────────────────────────────

// Called once from init.js after a session exists. The markup lives in
// portal.html alongside #modal and #toast; this only wires it.
export function mountAssistant(enabled) {
  ASK.enabled = !!enabled;

  document.getElementById('assistantLauncher')?.addEventListener('click', toggleAssistant);
  document.getElementById('assistantClose')?.addEventListener('click', () => {
    closeAssistant();
    document.getElementById('assistantLauncher')?.focus();
  });
  document.getElementById('assistantForm')?.addEventListener('submit', e => {
    e.preventDefault();
    submit();
  });

  const input = document.getElementById('assistantInput');
  input?.addEventListener('input', autosize);
  input?.addEventListener('keydown', onKeydown);

  document.getElementById('assistantClear')?.addEventListener('click', () => {
    ASK.thread = [];
    renderThread();
    if (input) { input.value = ''; autosize(); input.focus(); }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && ASK.open) {
      closeAssistant();
      document.getElementById('assistantLauncher')?.focus();
    }
  });

  renderThread();
  syncAssistant();
}

Object.assign(window, { syncAssistant, openAssistant, toggleAssistant });
